import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Drawer from "../../../components/common/Drawer";
import Input from "../../../components/common/Input";
import TextArea from "../../../components/common/TextArea";
import Checkbox from "../../../components/common/Checkbox";
import Button from "../../../components/common/Button";
import RichTextEditor from "../../../components/common/RichTextEditor";
import Tab from "../../../components/common/Tab";
import {
  platformOptions,
  channelDisplayConfig,
} from "../constants/notificationConstants";
import {
  validateImageUrl,
  validateActionUrl,
} from "../../../services/notificationTemplateService";
import { notifyWarning, notifyError } from "../../../utils/notification";
import { fetchChannelStatusThunk } from "../../../store/thunks/campaignThunks";

const ALL_CHANNELS = ["FCM", "APNS", "WEB", "WHATSAPP"];

const TemplateFormDrawer = ({
  isOpen,
  onClose,
  template,
  onSave,
  onSaveAndBumpVersion,
  isEditing = false,
}) => {
  // ---------------------------------------------------------------------------
  // STATE ---------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  const dispatch = useDispatch();
  const { status: channelStatus } = useSelector(
    (state) => state.notificationCampaigns.channels
  );

  const [formData, setFormData] = useState({
    key: "",
    version: 1,
    locale: "en-IN", // Backend default locale
    name: "", // Display only
    description: "", // Display only
    isPromotional: false,
    channels: ALL_CHANNELS, // <- single source of truth for platform selection
    archived: false,
    content: {
      titleTpl: "",
      bodyTpl: "",
      imageUrlTpl: "",
      iconUrlTpl: "",
      deeplinkTpl: "",
      actions: [],
    },
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [urlValidation, setUrlValidation] = useState({
    image: null,
    icon: null,
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Fetch channel status when component mounts
  useEffect(() => {
    dispatch(fetchChannelStatusThunk());
  }, [dispatch]);

  // ---------------------------------------------------------------------------
  // EFFECTS -------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (template) {
      setFormData({
        key: template.key,
        version: template.version,
        locale: template.locale || "en-IN",
        name: template.name || template.key,
        description: template.description || "",
        isPromotional: template.isPromotional,
        channels: template.channels?.length ? template.channels : ALL_CHANNELS,
        archived: template.archived || false,
        content: {
          titleTpl: template.titleTpl || "",
          bodyTpl: template.bodyTpl || "",
          imageUrlTpl: template.imageUrlTpl || "",
          iconUrlTpl: template.iconUrlTpl || "",
          deeplinkTpl: template.deeplinkTpl || "",
          actions:
            template.actions?.map(({ labelTpl, deeplinkTpl }) => ({
              labelTpl,
              deeplinkTpl,
            })) || [],
        },
      });
    }
  }, [template]);

  // Get the enabled channels for filtering
  const getEnabledChannels = useMemo(() => {
    return Object.entries(channelStatus)
      .filter(([_, enabled]) => enabled)
      .map(([channel]) => channel);
  }, [channelStatus]);

  const hasOnlyOneChannel = getEnabledChannels.length === 1;

  // Auto-select the single channel if config says to
  useEffect(() => {
    if (
      channelDisplayConfig.autoSelectSingleChannel &&
      hasOnlyOneChannel &&
      getEnabledChannels.length > 0
    ) {
      const onlyEnabledChannel = getEnabledChannels[0];
      if (!formData.channels.includes(onlyEnabledChannel)) {
        setFormData((prev) => ({
          ...prev,
          channels: [onlyEnabledChannel],
        }));
      }
    }
  }, [channelStatus, formData.channels, getEnabledChannels, hasOnlyOneChannel]);

  // ---------------------------------------------------------------------------
  // VALIDATION ----------------------------------------------------------------
  // ---------------------------------------------------------------------------
  const validateField = (field, value) => {
    let error = "";

    switch (field) {
      case "key":
        if (!value.trim()) {
          error = "Template key is required";
        } else if (!/^[A-Z0-9_]+$/.test(value)) {
          error =
            "Template key must contain only uppercase letters, numbers, and underscores";
        } else if (value.length < 3) {
          error = "Template key must be at least 3 characters long";
        }
        break;

      case "name":
        if (!value.trim()) {
          error = "Template name is required";
        } else if (value.length < 3) {
          error = "Template name must be at least 3 characters long";
        }
        break;

      case "locale":
        if (!value.trim()) {
          error = "Locale is required";
        } else if (!/^[a-z]{2}(-[A-Z]{2})?$/.test(value)) {
          error = "Locale must be in format 'en' or 'en-US'";
        }
        break;

      case "content.titleTpl":
        if (!value.trim()) {
          error = "Title is required";
        } else if (value.length > 100) {
          error = "Title must be less than 100 characters";
        }
        break;

      case "content.bodyTpl":
        if (!value.trim()) {
          error = "Body is required";
        } else if (value.length > 1000) {
          error = "Body must be less than 1000 characters";
        }
        break;

      case "content.imageUrlTpl":
        if (value && !isValidUrl(value)) {
          error = "Invalid image URL format";
        }
        break;

      case "content.iconUrlTpl":
        if (value && !isValidUrl(value)) {
          error = "Invalid icon URL format";
        }
        break;

      case "content.deeplinkTpl":
        if (value && !isValidUrl(value) && !isValidDeeplink(value)) {
          error = "Invalid deeplink format";
        }
        break;

      case "channels":
        if (!value || value.length === 0) {
          error = "At least one channel must be selected";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const isValidUrl = (url) => {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  };

  const isValidDeeplink = (deeplink) => {
    // Allow app scheme deeplinks like myapp://path
    return /^[a-zA-Z][a-zA-Z0-9+.-]*:\/\//.test(deeplink);
  };

  const validateActionField = (actionIndex, field, value) => {
    let error = "";

    switch (field) {
      case "labelTpl":
        if (!value.trim()) {
          error = "Action label is required";
        } else if (value.length > 50) {
          error = "Action label must be less than 50 characters";
        }
        break;

      case "deeplinkTpl":
        if (!value.trim()) {
          error = "Action URL is required";
        } else if (!isValidUrl(value) && !isValidDeeplink(value)) {
          error = "Invalid URL format";
        }
        break;

      default:
        break;
    }

    return error;
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate basic fields
    const basicFields = [
      "key",
      "name",
      "locale",
      "content.titleTpl",
      "content.bodyTpl",
    ];
    basicFields.forEach((field) => {
      const value = field.includes(".")
        ? field.split(".").reduce((obj, key) => obj?.[key], formData)
        : formData[field];
      const error = validateField(field, value);
      if (error) {
        newErrors[field] = error;
      }
    });

    // Validate optional URL fields
    const urlFields = [
      "content.imageUrlTpl",
      "content.iconUrlTpl",
      "content.deeplinkTpl",
    ];
    urlFields.forEach((field) => {
      const value = field.split(".").reduce((obj, key) => obj?.[key], formData);
      if (value) {
        const error = validateField(field, value);
        if (error) {
          newErrors[field] = error;
        }
      }
    });

    // Validate channels
    const channelsError = validateField("channels", formData.channels);
    if (channelsError) {
      newErrors.channels = channelsError;
    }

    // Validate actions
    formData.content.actions.forEach((action, index) => {
      const labelError = validateActionField(
        index,
        "labelTpl",
        action.labelTpl
      );
      const urlError = validateActionField(
        index,
        "deeplinkTpl",
        action.deeplinkTpl
      );

      if (labelError) {
        newErrors[`actions.${index}.labelTpl`] = labelError;
      }
      if (urlError) {
        newErrors[`actions.${index}.deeplinkTpl`] = urlError;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // ---------------------------------------------------------------------------
  // HANDLERS ------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  const handleTabChange = (index) => setActiveTab(index);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }

    // Mark field as touched
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleContentChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        [field]: value,
      },
    }));

    const fieldKey = `content.${field}`;

    // Clear error for this field
    if (errors[fieldKey]) {
      setErrors((prev) => ({ ...prev, [fieldKey]: "" }));
    }

    // Mark field as touched
    setTouched((prev) => ({ ...prev, [fieldKey]: true }));
  };

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));

    const value = field.includes(".")
      ? field.split(".").reduce((obj, key) => obj?.[key], formData)
      : formData[field];

    const error = validateField(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  /**
   * Toggle one of the channel chips. When the special "all" chip is selected
   * we store it as the sole item in `channels`. Before submitting we will
   * convert ["all"] back to the concrete list expected by the backend.
   */
  const handleChannelToggle = (channel) => {
    // If we have only one channel enabled and auto-select is on, don't allow toggling
    if (channelDisplayConfig.autoSelectSingleChannel && hasOnlyOneChannel) {
      return;
    }

    setFormData((prev) => {
      let channels = [...prev.channels];

      // Special case: user clicked the "All Platforms" option
      if (channel === "all") {
        // Only include enabled channels when selecting "all"
        return {
          ...prev,
          channels: ["all"],
        };
      }

      // If "all" is currently selected remove it because a specific
      // platform is now being toggled.
      const allIndex = channels.indexOf("all");
      if (allIndex > -1) channels.splice(allIndex, 1);

      // Toggle the individual channel
      const idx = channels.indexOf(channel);
      if (idx > -1) {
        channels.splice(idx, 1);
      } else {
        channels.push(channel);
      }

      // Fallback: if nothing is selected ensure "all" is saved so there is
      // always at least one value.
      return { ...prev, channels: channels.length ? channels : ["all"] };
    });

    // Clear channels error
    if (errors.channels) {
      setErrors((prev) => ({ ...prev, channels: "" }));
    }
  };

  const validateUrls = async () => {
    const validations = {};

    if (formData.content.imageUrlTpl) {
      const isValid = await validateImageUrl(formData.content.imageUrlTpl);
      validations.image = isValid;
      if (!isValid) {
        notifyWarning("Image URL is not valid or accessible");
        setErrors((prev) => ({
          ...prev,
          "content.imageUrlTpl": "Image URL is not accessible",
        }));
      }
    }

    if (formData.content.iconUrlTpl) {
      const isValid = await validateImageUrl(formData.content.iconUrlTpl);
      validations.icon = isValid;
      if (!isValid) {
        notifyWarning("Icon URL is not valid or accessible");
        setErrors((prev) => ({
          ...prev,
          "content.iconUrlTpl": "Icon URL is not accessible",
        }));
      }
    }

    // Validate action URLs
    for (let i = 0; i < formData.content.actions.length; i++) {
      const action = formData.content.actions[i];
      if (action.deeplinkTpl && isValidUrl(action.deeplinkTpl)) {
        const isValid = await validateActionUrl(action.deeplinkTpl);
        if (!isValid) {
          notifyWarning(`Action ${i + 1} URL is not accessible`);
          setErrors((prev) => ({
            ...prev,
            [`actions.${i}.deeplinkTpl`]: "URL is not accessible",
          }));
        }
      }
    }

    setUrlValidation(validations);
    return Object.values(validations).every((v) => v !== false);
  };

  const handleAddAction = () => {
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        actions: [...prev.content.actions, { labelTpl: "", deeplinkTpl: "" }],
      },
    }));
  };

  const handleActionChange = (index, field, value) => {
    const actions = [...formData.content.actions];
    actions[index][field] = value;
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        actions,
      },
    }));

    // Clear error for this action field
    const errorKey = `actions.${index}.${field}`;
    if (errors[errorKey]) {
      setErrors((prev) => ({ ...prev, [errorKey]: "" }));
    }

    // Mark field as touched
    setTouched((prev) => ({ ...prev, [errorKey]: true }));
  };

  const handleActionBlur = (index, field) => {
    const value = formData.content.actions[index][field];
    const error = validateActionField(index, field, value);
    const errorKey = `actions.${index}.${field}`;

    setTouched((prev) => ({ ...prev, [errorKey]: true }));
    setErrors((prev) => ({ ...prev, [errorKey]: error }));
  };

  const handleRemoveAction = (index) => {
    const actions = formData.content.actions.filter((_, i) => i !== index);
    setFormData((prev) => ({
      ...prev,
      content: {
        ...prev.content,
        actions,
      },
    }));

    // Clear errors for removed action
    const newErrors = { ...errors };
    Object.keys(newErrors).forEach((key) => {
      if (key.startsWith(`actions.${index}.`)) {
        delete newErrors[key];
      }
    });

    // Update error keys for remaining actions
    const updatedErrors = {};
    Object.keys(newErrors).forEach((key) => {
      if (key.startsWith("actions.")) {
        const parts = key.split(".");
        const actionIndex = parseInt(parts[1]);
        if (actionIndex > index) {
          const newKey = `actions.${actionIndex - 1}.${parts[2]}`;
          updatedErrors[newKey] = newErrors[key];
        } else {
          updatedErrors[key] = newErrors[key];
        }
      } else {
        updatedErrors[key] = newErrors[key];
      }
    });

    setErrors(updatedErrors);
  };

  // ---------------------------------------------------------------------------
  // SUBMIT --------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  const handleSubmit = async (bumpVersion = false) => {
    // Validate form first
    if (!validateForm()) {
      notifyError("Please fix the validation errors before submitting");

      // Focus on first tab with errors
      const errorKeys = Object.keys(errors);
      if (
        errorKeys.some((key) =>
          ["key", "name", "locale", "channels"].includes(key)
        )
      ) {
        setActiveTab(0);
      } else if (errorKeys.some((key) => key.startsWith("content."))) {
        setActiveTab(1);
      } else if (errorKeys.some((key) => key.startsWith("actions."))) {
        setActiveTab(2);
      }

      return;
    }

    setLoading(true);

    try {
      // Validate URLs
      if (!(await validateUrls())) {
        setLoading(false);
        return;
      }

      // Convert ["all"] sentinel back to concrete list
      // If we're auto-selecting a single channel, don't use "all" mode
      const selectedChannels =
        formData.channels.includes("all") && !hasOnlyOneChannel
          ? Object.entries(channelStatus)
              .filter(([_, enabled]) => enabled)
              .map(([channel]) => channel)
          : formData.channels;

      const payload = {
        key: formData.key.trim(),
        locale: formData.locale.trim(),
        titleTpl: formData.content.titleTpl.trim(),
        bodyTpl: formData.content.bodyTpl.trim(),
        imageUrlTpl: formData.content.imageUrlTpl.trim() || undefined,
        iconUrlTpl: formData.content.iconUrlTpl.trim() || undefined,
        deeplinkTpl: formData.content.deeplinkTpl.trim() || undefined,
        actions: formData.content.actions.map((action) => ({
          labelTpl: action.labelTpl.trim(),
          deeplinkTpl: action.deeplinkTpl.trim(),
        })),
        isPromotional: formData.isPromotional,
        channels: selectedChannels,
        archived: formData.archived,
      };

      // Only include version for existing templates (updates)
      if (template) {
        // payload.version = formData.version;
      }

      // Remove undefined fields to avoid sending empty strings
      Object.keys(payload).forEach((key) => {
        if (payload[key] === undefined || payload[key] === "") {
          delete payload[key];
        }
      });

      // Call the appropriate save function
      if (bumpVersion && onSaveAndBumpVersion) {
        await onSaveAndBumpVersion(payload);
      } else {
        await onSave(payload);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      // Handle server validation errors
      if (error.response?.data?.errors) {
        const serverErrors = {};
        error.response.data.errors.forEach((err) => {
          // Map server field names to form field names
          const fieldMapping = {
            titleTpl: "content.titleTpl",
            bodyTpl: "content.bodyTpl",
            imageUrlTpl: "content.imageUrlTpl",
            iconUrlTpl: "content.iconUrlTpl",
            deeplinkTpl: "content.deeplinkTpl",
            key: "key",
            locale: "locale",
          };

          const formField = fieldMapping[err.field] || err.field;
          serverErrors[formField] = err.message;
        });

        setErrors((prev) => ({ ...prev, ...serverErrors }));
        notifyError("Please fix the validation errors");
      } else {
        notifyError(error.message || "Failed to save template");
      }
    }
  };

  // Helper function to get error message for a field
  const getFieldError = (field) => {
    return touched[field] && errors[field] ? errors[field] : "";
  };

  // Map platform options to channel values
  const platformToChannel = {
    all: "all",
    FCM: "FCM",
    APNS: "APNS",
    WEB: "WEB",
    WHATSAPP: "WHATSAPP",
  };

  // Filter platform options based on configuration
  const visiblePlatformOptions = useMemo(() => {
    // Start with all options
    let options = [...platformOptions];

    // If we're set to hide disabled channels
    if (!channelDisplayConfig.showDisabled) {
      // Filter out options for disabled channels
      options = options.filter((opt) => {
        const channelKey = platformToChannel[opt.value];
        return channelKey === "all" || channelStatus[channelKey];
      });
    }

    // If we're auto-selecting the single channel and there's only one
    if (channelDisplayConfig.autoSelectSingleChannel && hasOnlyOneChannel) {
      // Hide the "all" option
      options = options.filter((opt) => opt.value !== "all");
    }

    return options;
  }, [channelStatus, hasOnlyOneChannel]);

  // When in the Basic Info tab, show channels with disabled state
  const renderChannelsSection = () => {
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Channels <span className="text-red-500">*</span>
        </label>
        <div className="flex flex-wrap gap-2">
          {visiblePlatformOptions.map((option) => {
            // Check if this channel is enabled
            const channelKey = platformToChannel[option.value];
            const isEnabled =
              channelKey === "all" ? true : channelStatus[channelKey];

            // Skip if we're configured to hide disabled channels and this one is disabled
            if (!channelDisplayConfig.showDisabled && !isEnabled) {
              return null;
            }

            // For auto-selected single channel, always show as selected and disabled toggle
            const isForceSelected =
              channelDisplayConfig.autoSelectSingleChannel &&
              hasOnlyOneChannel &&
              channelKey !== "all" &&
              isEnabled;

            // Prevent disabled channels from being selected
            const isSelected =
              isEnabled &&
              (formData.channels.includes(option.value) || isForceSelected);

            return (
              <div key={option.value} className={isEnabled ? "" : "relative"}>
                <label
                  className={`flex items-center ${
                    isEnabled && !isForceSelected
                      ? "cursor-pointer"
                      : "cursor-not-allowed"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() =>
                      isEnabled &&
                      !isForceSelected &&
                      handleChannelToggle(option.value)
                    }
                    className="sr-only"
                    disabled={!isEnabled || isForceSelected}
                  />
                  <span
                    className={`px-3 py-1 rounded-full text-sm transition-colors ${
                      isSelected
                        ? "bg-primary text-white"
                        : isEnabled
                        ? "bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        : "bg-gray-100 dark:bg-gray-800 text-gray-400 dark:text-gray-500 border border-gray-300 dark:border-gray-700"
                    }`}
                  >
                    {option.label}
                    {!isEnabled && (
                      <span className="ml-1 text-xs">(Disabled)</span>
                    )}
                    {isForceSelected && (
                      <span className="ml-1 text-xs">(Only available)</span>
                    )}
                  </span>
                </label>
              </div>
            );
          })}
        </div>
        {errors.channels && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            {errors.channels}
          </p>
        )}
        <p className="mt-2 text-xs text-gray-500">
          {Object.values(channelStatus).some((enabled) => !enabled)
            ? "Disabled channels are not available for this account"
            : "All channels are available for this account"}
        </p>
      </div>
    );
  };

  // ---------------------------------------------------------------------------
  // TABS ----------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  const tabs = [
    {
      label: "Basic Info",
      content: (
        <div className="space-y-4">
          <Input
            label="Template Key"
            value={formData.key}
            onChange={(e) =>
              handleInputChange("key", e.target.value.toUpperCase())
            }
            onBlur={() => handleBlur("key")}
            placeholder="e.g., WELCOME_NOTIFICATION"
            disabled={!!template}
            required
            error={getFieldError("key")}
          />
          <Input
            label="Template Name"
            value={formData.name}
            onChange={(e) => handleInputChange("name", e.target.value)}
            onBlur={() => handleBlur("name")}
            placeholder="e.g., Welcome Notification"
            required
            error={getFieldError("name")}
          />
          <TextArea
            label="Description"
            value={formData.description}
            onChange={(e) => handleInputChange("description", e.target.value)}
            placeholder="Describe the purpose of this template..."
            rows={3}
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Locale"
              value={formData.locale}
              onChange={(e) => handleInputChange("locale", e.target.value)}
              onBlur={() => handleBlur("locale")}
              placeholder="e.g., en-IN, hi-IN"
              required
              error={getFieldError("locale")}
            />
            <div className="flex items-end">
              <Checkbox
                label="Promotional"
                checked={formData.isPromotional}
                onChange={(e) =>
                  handleInputChange("isPromotional", e.target.checked)
                }
              />
            </div>
          </div>
          {/* Channels with disabled states */}
          {renderChannelsSection()}
        </div>
      ),
    },
    {
      label: "Content",
      content: (
        <div className="space-y-4">
          <Input
            label="Title"
            value={formData.content.titleTpl}
            onChange={(e) => handleContentChange("titleTpl", e.target.value)}
            onBlur={() => handleBlur("content.titleTpl")}
            placeholder="e.g., Welcome to {{appName}}!"
            required
            error={getFieldError("content.titleTpl")}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Body <span className="text-red-500">*</span>
            </label>
            <RichTextEditor
              value={formData.content.bodyTpl}
              onChange={(value) => handleContentChange("bodyTpl", value)}
              onBlur={() => handleBlur("content.bodyTpl")}
              placeholder="Write your notification message..."
              maxLength={1000}
              error={getFieldError("content.bodyTpl")}
            />
            {errors["content.bodyTpl"] && (
              <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                {errors["content.bodyTpl"]}
              </p>
            )}
          </div>
          <Input
            label="Image URL"
            value={formData.content.imageUrlTpl}
            onChange={(e) => handleContentChange("imageUrlTpl", e.target.value)}
            onBlur={() => handleBlur("content.imageUrlTpl")}
            placeholder="https://example.com/image.jpg"
            error={getFieldError("content.imageUrlTpl")}
          />
          <Input
            label="Icon URL"
            value={formData.content.iconUrlTpl}
            onChange={(e) => handleContentChange("iconUrlTpl", e.target.value)}
            onBlur={() => handleBlur("content.iconUrlTpl")}
            placeholder="https://example.com/icon.png"
            error={getFieldError("content.iconUrlTpl")}
          />
          <Input
            label="Deeplink"
            value={formData.content.deeplinkTpl}
            onChange={(e) => handleContentChange("deeplinkTpl", e.target.value)}
            onBlur={() => handleBlur("content.deeplinkTpl")}
            placeholder="https://example.com/action or myapp://action"
            error={getFieldError("content.deeplinkTpl")}
          />
        </div>
      ),
    },
    {
      label: "Actions",
      content: (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-2">
            <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Action Buttons
            </label>
            <Button
              variant="outline"
              size="sm"
              onClick={handleAddAction}
              disabled={formData.content.actions.length >= 3}
            >
              Add Action
            </Button>
          </div>
          {formData.content.actions.map((action, index) => (
            <div
              key={index}
              className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3"
            >
              <div className="flex justify-between items-center">
                <span className="text-sm font-medium">Action {index + 1}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveAction(index)}
                >
                  Remove
                </Button>
              </div>
              <Input
                label="Button Label"
                value={action.labelTpl}
                onChange={(e) =>
                  handleActionChange(index, "labelTpl", e.target.value)
                }
                onBlur={() => handleActionBlur(index, "labelTpl")}
                placeholder="e.g., View Details"
                required
                error={getFieldError(`actions.${index}.labelTpl`)}
              />
              <Input
                label="URL"
                value={action.deeplinkTpl}
                onChange={(e) =>
                  handleActionChange(index, "deeplinkTpl", e.target.value)
                }
                onBlur={() => handleActionBlur(index, "deeplinkTpl")}
                placeholder="https://example.com/action or myapp://action"
                required
                error={getFieldError(`actions.${index}.deeplinkTpl`)}
              />
            </div>
          ))}
          {formData.content.actions.length === 0 && (
            <p className="text-center text-gray-500 dark:text-gray-400 py-4">
              No actions added. Click "Add Action" to create one.
            </p>
          )}
        </div>
      ),
    },
  ];

  // ---------------------------------------------------------------------------
  // RENDER --------------------------------------------------------------------
  // ---------------------------------------------------------------------------
  return (
    <Drawer
      isOpen={isOpen}
      onClose={onClose}
      title={template ? "Edit Template" : "Create Template"}
      size="lg"
    >
      <div className="flex-1 overflow-y-auto">
        <Tab tabs={tabs} activeTab={activeTab} onTabChange={handleTabChange} />
      </div>
      <div className="flex justify-end gap-2 p-4 border-t border-gray-200 dark:border-gray-700">
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
        {isEditing && onSaveAndBumpVersion && (
          <Button
            variant="outline"
            onClick={() => handleSubmit(true)}
            loading={loading}
          >
            Save & Bump Version
          </Button>
        )}
        <Button
          variant="primary"
          onClick={() => handleSubmit(false)}
          loading={loading}
        >
          {template ? "Update Template" : "Create Template"}
        </Button>
      </div>
    </Drawer>
  );
};

export default TemplateFormDrawer;
